import {
    gql,
    QueryHookOptions,
    useApolloClient,
    useLazyQuery,
    useQuery,
} from '@apollo/client'
import { cartFragment } from '@emico-hooks/cart-fragment'
import { useCartId } from '@emico-hooks/cart-id'
import { useAuthorizationContext } from '@emico-hooks/login-token'
import { useCallback } from 'react'

import {
    ApplyCouponToCartOutput,
    CustomerCoupon as GeneratedCustomerCoupon,
    Query,
} from '../../../../graphql/schema.generated'
import { useFilterNullValues } from '../../../../utils/filterNullValues'

export type CustomerCoupon = GeneratedCustomerCoupon & {
    image?: string
    valuePercentage?: number | undefined
    valueCurrency?: string | number
    originalMinimalOrderAmount?: number
}

export const query = gql`
    query GetCustomerCoupons {
        customerCoupon {
            items {
                couponId
                amount {
                    currency
                    value
                }
                applied
                isActive
                code
                description
                type {
                    description
                    image
                    title
                }
                minimalOrderAmount {
                    currency
                    value
                }
                validUntil
            }
        }
    }
`

type QueryResult = Pick<Query, 'customerCoupon'>

export const useGetCustomerCoupons = (
    options?: QueryHookOptions<QueryResult>,
) => {
    const authorizationContext = useAuthorizationContext()
    const { data, error, loading, refetch } = useQuery<QueryResult>(query, {
        fetchPolicy: 'cache-and-network',
        ...options,
        context: authorizationContext,
    })

    return {
        data: useFilterNullValues(data?.customerCoupon?.items),
        loading,
        refetch,
        error,
    }
}

export const useLazyGetCustomerCoupons = (options?: QueryHookOptions) => {
    const authorizationContext = useAuthorizationContext()
    // eslint-disable-next-line no-restricted-syntax
    const [getCustomerCoupons, { called, data, error, loading }] =
        // eslint-disable-next-line no-restricted-syntax
        useLazyQuery<QueryResult>(query, {
            fetchPolicy: 'cache-and-network',
            ...options,
            context: authorizationContext,
        })

    return {
        data: useFilterNullValues(data?.customerCoupon?.items),
        loading,
        called,
        error,
        getCustomerCoupons,
    }
}

export const useUnclaimedVouchersCount = (): number | undefined => {
    const { data: vouchers } = useGetCustomerCoupons()

    return vouchers?.reduce((sum, item) => sum + (item?.applied ? 0 : 1), 0)
}

const applyCouponMutation = gql`
    mutation ApplyCouponToCart($input: ApplyCouponToCartInput) {
        applyCouponToCart(input: $input) {
            cart {
                ...CartFragment
            }
        }
    }
    ${cartFragment}
`

export const useApplyCoupon = () => {
    const client = useApolloClient()
    const authorizationContext = useAuthorizationContext()
    const cartId = useCartId()

    return useCallback(
        async (couponCode: string) => {
            if (!cartId) {
                return
            }

            return client.mutate<ApplyCouponToCartOutput>({
                mutation: applyCouponMutation,
                variables: {
                    input: {
                        cart_id: cartId,
                        coupon_code: couponCode,
                    },
                },
                context: authorizationContext,
            })
        },
        [authorizationContext, cartId, client],
    )
}
